import { effectScope, watch } from 'vue'
import { useMenuTranslateState } from 'src/services/menuTranslateState'
import { translateText } from 'src/services/aiTranslator'

const menuTranslateState = useMenuTranslateState()

const ORIGINAL_ATTR = 'data-ai-translate-original'
const RUN_ID_ATTR = 'data-ai-translate-runid'
const SCOPE_KEY = Symbol('ai-translate-scope')

function getOriginal(el) {
  const stored = el.getAttribute(ORIGINAL_ATTR)
  if (stored != null) return stored
  const original = (el.textContent ?? '').toString()
  el.setAttribute(ORIGINAL_ATTR, original)
  return original
}

function setText(el, value) {
  el.textContent = value
}

function nextRunId(el) {
  const prev = Number(el.getAttribute(RUN_ID_ATTR) || '0')
  const next = prev + 1
  el.setAttribute(RUN_ID_ATTR, String(next))
  return next
}

function resolveOptions(binding) {
  // global defaults
  let enabled = menuTranslateState.enabled
  let to = menuTranslateState.targetLang

  const v = binding?.value
  if (typeof v === 'string') {
    to = v
  } else if (v && typeof v === 'object') {
    if (typeof v.enabled === 'boolean') enabled = v.enabled
    if (typeof v.to === 'string') to = v.to
  }

  return { enabled, to }
}

async function apply(el, binding) {
  const original = getOriginal(el)
  const { enabled, to } = resolveOptions(binding)

  if (!enabled || !to) {
    nextRunId(el) // invalidate any in-flight async translation
    setText(el, original)
    return
  }
  if (!original.trim()) {
    nextRunId(el) // invalidate any in-flight async translation
    setText(el, original)
    return
  }

  const runId = nextRunId(el)
  try {
    const translated = await translateText({ text: original, to })
    const current = Number(el.getAttribute(RUN_ID_ATTR) || '0')
    if (current !== runId) return
    setText(el, translated)
  } catch {
    const current = Number(el.getAttribute(RUN_ID_ATTR) || '0')
    if (current !== runId) return
    setText(el, original)
  }
}

export default {
  mounted(el, binding) {
    getOriginal(el)
    void apply(el, binding)

    // Create reactive watchers safely with teardown
    const scope = effectScope()
    scope.run(() => {
      // watch global state; immediate false because we already applied once
      watch(
        () => [menuTranslateState.enabled, menuTranslateState.targetLang],
        () => { void apply(el, binding) },
        { deep: false }
      )
    })

    el[SCOPE_KEY] = scope
  },

  updated(el, binding) {
    // If slot text changed BEFORE first store, you must decide behavior.
    // We keep ORIGINAL stable to avoid translating "already translated" text.
    // But if you truly need to update original when source changes, expose a mode.
    void apply(el, binding)
  },

  beforeUnmount(el) {
    const scope = el[SCOPE_KEY]
    if (scope) scope.stop()
    el[SCOPE_KEY] = null
  }
}
